import React, { createContext, useEffect, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { FIELD_TYPES } from "base/constants";
import { formatToUserTimeZone, formatToLocale } from "base/utils";
import { i18n } from "base/i18n";

import { ShowFiles } from "./files.renderer.jsx";
import { OverflowText } from "@widgets/datadisplay/overflowtext/index.jsx";
import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  ICON_BACKGROUND,
  ICON_HOVER_TYPE,
  ICON_PADDING,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import lookupStyles from "./lookup.css";

export const LOOKUP_CARD_SIZE = {
  BASE: "base",
  SMALL: "small"
};

export const LookupCardContext = createContext();

export function LookupCard(props) {
  const {
    option,
    onClick,
    columns,
    showClearIcon,
    onClear,
    className,
    size = LOOKUP_CARD_SIZE.BASE,
    actionPath,
    isDropDownCard,
    customProps,
    columnLayout,
    cellWidth,
    hiddenFields,
    isError,
    scrollable = false,
    scrollableHeight,
    focusOnMount,
    headerKey,
    onBlur
  } = props;

  const lookupCardRef = useRef(null);

  useEffect(
    function focusMount() {
      if (focusOnMount) lookupCardRef.current.focus();
    },
    [focusOnMount]
  );

  const getAttachmentCount = (columEnd, cellWidth) => {
    if (cellWidth) {
      return Math.ceil((cellWidth - 10) / 100);
    } else if (columEnd) {
      let ddCard = 6;
      let end = Number(columEnd?.slice(-1)) || 3;

      if (end <= 2) {
        end = 2;
      } else if (end <= 4) {
        end += 2;
      } else if (end <= 6) {
        ddCard = end + 3;
        end += 3;
      }
      return isDropDownCard ? ddCard : end;
    } else {
      return 3;
    }
  };

  const contextValue = {
    isPublicForm: Boolean(actionPath || customProps?.isPublicForm),
    attachmentCount: getAttachmentCount(columnLayout, cellWidth)
  };

  return (
    <div
      className={`${
        showClearIcon ? lookupStyles.showBorder : lookupStyles.readOnly
      }
      ${isError ? lookupStyles.cardError : ""}`}
      ref={lookupCardRef}
      tabIndex={0}
      onBlur={onBlur}
    >
      <div
        className={`${
          size === LOOKUP_CARD_SIZE.SMALL
            ? lookupStyles.lookupFieldDataSmall
            : lookupStyles.lookupFieldData
        } ${scrollable ? lookupStyles.hasScroll : ""}
        ${
          isDropDownCard
            ? lookupStyles.unSelectedCard
            : lookupStyles.lookupSelectedData
        }
          ${showClearIcon ? lookupStyles.hasClear : ""} ${className}`}
        onClick={onClick}
      >
        {hiddenFields.indexOf(headerKey) === -1 && option[headerKey] && (
          <CardHeader headerName={option[headerKey]} size={size} />
        )}
        {scrollable ? (
          <div
            style={{
              maxHeight: scrollableHeight,
              overflowY: "auto"
            }}
            className={lookupStyles.cardBodyScrollWrapper}
          >
            <CardBody
              option={option}
              columns={columns}
              hiddenFields={hiddenFields}
              contextValue={contextValue}
              columnLayout={columnLayout}
            />
          </div>
        ) : (
          <CardBody
            option={option}
            columns={columns}
            hiddenFields={hiddenFields}
            contextValue={contextValue}
            columnLayout={columnLayout}
          />
        )}
        {showClearIcon && (
          <KfIcon
            onClick={onClear}
            icon={KF_ICONS.CLOSE}
            stroke={ICON_STROKE.THIN}
            size={ICON_SIZE.SIZE_12}
            hoverType={ICON_HOVER_TYPE.LIGHT_GRAY}
            padding={ICON_PADDING.PADDING_4}
            backgroundType={ICON_BACKGROUND.SOLID}
            className={`${lookupStyles.clearIcon} shadow-4dp`}
          />
        )}
      </div>
    </div>
  );
}

function CardHeader(prop) {
  var { headerName, size } = prop;
  return (
    <div className={lookupStyles.cardHeader}>
      <div
        className={`${
          size === LOOKUP_CARD_SIZE.SMALL
            ? lookupStyles.lookupSubjectSmall
            : lookupStyles.lookupSubject
        }`}
      >
        <OverflowText line={2}>{headerName}</OverflowText>
      </div>
    </div>
  );
}

function CardBody(prop) {
  const { option, columns, hiddenFields, columnLayout, contextValue } = prop;

  return (
    <div
      className={`${lookupStyles.fieldData} ${
        columnLayout === "e1" ? lookupStyles.singleColumn : ""
      }`}
    >
      {option &&
        columns?.map(
          (column) =>
            hiddenFields.indexOf(column["Id"]) === -1 &&
            column["Id"] !== "_id" &&
            column["Id"] !== "Name" && (
              <LookupCardContext.Provider value={contextValue} key={column.Id}>
                <CheckLookupFieldType
                  label={column["Name"]}
                  value={option[column["Id"]]}
                  type={column.Type}
                  id={column["Id"]}
                />
              </LookupCardContext.Provider>
            )
        )}
    </div>
  );
}

function CheckLookupFieldType(prop) {
  var { label, value, type, id } = prop;

  var restrictedFields = ["Reference", "Signature"];

  var newValue = value;

  if (type === FIELD_TYPES.BOOLEAN) {
    newValue = value === true ? "Yes" : "No";
  } else if (
    type === FIELD_TYPES.MULTI_SELECT ||
    type === FIELD_TYPES.CHECK_BOX ||
    type === FIELD_TYPES.STRING_LIST
  ) {
    newValue = getListOfValues(value);
  } else if (
    type === FIELD_TYPES.MULTI_USER ||
    type === FIELD_TYPES.USERGROUPLIST
  ) {
    newValue = getListOfObjValue(value, "Name");
  } else if (type === FIELD_TYPES.CHECK_LIST) {
    newValue = getListOfObjValue(value, "value");
  } else if (id === "_created_by" || id === "_modified_by" || type === "User") {
    newValue = value && value["Name"];
  } else if (type === FIELD_TYPES.DATE || type === FIELD_TYPES.DATE_TIME) {
    newValue =
      value &&
      (type === FIELD_TYPES.DATE_TIME
        ? formatToUserTimeZone(value)
        : formatToLocale(value, false));
  } else if (type === FIELD_TYPES.IMAGE || type === FIELD_TYPES.ATTACHMENT) {
    newValue = (value && <ShowFiles value={value} type={type} />) || "N/A";
  } else if (restrictedFields.includes(type)) {
    return null;
  } else if (value !== null && typeof value === "object") {
    return null;
  }

  return (
    <div className={lookupStyles.record}>
      <div className={`${lookupStyles.fieldName}`}>{i18n._(label)}</div>
      <div className={`${lookupStyles.fieldValue}`}>
        {!(newValue === null || newValue === "" || newValue === undefined)
          ? newValue
          : "N/A"}
      </div>
    </div>
  );
}

const Showvalue = (props) => {
  var { value, type } = props;

  if (type === "Image" || type === "Attachment") {
    return <ShowFiles {...props} />;
  } else {
    return value || "N/A";
  }
};

Showvalue.propTypes = {
  value: PropTypes.string,
  type: PropTypes.string
};

const getListOfObjValue = (list, keyValue) => {
  if (list) {
    var listValues = list?.map((value) =>
      typeof value === "object" ? value[keyValue] : ""
    );
    return getListOfValues(listValues);
  }
};

const getListOfValues = (list) => {
  if (typeof list === "string") {
    return list;
  } else {
    list = list?.filter((element) => {
      return element !== false && element !== undefined;
    });
    return list?.toString();
  }
};

LookupCard.propTypes = {
  option: PropTypes.shape({
    Name: PropTypes.string,
    _id: PropTypes.string
  }),
  onClick: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Type: PropTypes.string,
      Name: PropTypes.string,
      Id: PropTypes.string
    })
  ),
  showClearIcon: PropTypes.bool,
  onClear: PropTypes.func,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  actionPath: PropTypes.string,
  isDropDownCard: PropTypes.bool,
  isError: PropTypes.bool,
  cellWidth: PropTypes.number,
  customProps: PropTypes.shape({
    activityInstanceId: PropTypes.string,
    adminBaseUrl: PropTypes.string,
    baseUrl: PropTypes.string,
    currentStep: PropTypes.string,
    flowId: PropTypes.string,
    flowType: PropTypes.string,
    idp: PropTypes.string, // Adjust the type based on the actual data type
    instanceId: PropTypes.string,
    isAdmin: PropTypes.bool,
    isSendBackToInitiator: PropTypes.bool, // Adjust the type based on the actual data type
    isStartStep: PropTypes.bool,
    parentInstanceId: PropTypes.string,
    parentModelId: PropTypes.string,
    parentResourceId: PropTypes.string,
    isPublicForm: PropTypes.bool,
    sharedWith: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        Kind: PropTypes.string.isRequired,
        Name: PropTypes.string.isRequired
      })
    ),
    showChildTablePreview: PropTypes.bool,
    status: PropTypes.string,
    type: PropTypes.string
  }),
  size: PropTypes.string,
  columnLayout: PropTypes.string,
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  scrollable: PropTypes.bool,
  scrollableHeight: PropTypes.string,
  focusOnMount: PropTypes.bool,
  headerKey: PropTypes.string,
  onBlur: PropTypes.func
};

LookupCard.defaultProps = {
  hiddenFields: [],
  headerKey: "Name"
};
